import React, { useEffect, useState } from 'react';
import {Button, Input, InputNumber, InputNumberProps, Select} from 'antd';
import {useNavigate, useParams} from "react-router-dom";
import {useStore} from "zustand";
import classNames from "classnames";
import {useMediaQuery} from "react-responsive";
import styles from './CashManage.module.scss';
import cashTxStore, { CreateCashTxType } from '../../stores/cashTxStore';
import { CashTx } from '../../types/CashTx';
import PageTitle from '../../components/common/PageTitle/PageTitle';
import LoadingBar from '../../components/common/LoadingBar/LoadingBar';
import SimplePaging from '../../components/common/paging/SimplePaging';
import CashApprovePopup from '../../components/specific/CashApprovePopup/CashApprovePopup';
import CashMakeRequestPopup from '../../components/specific/CashMakeRequestPopup/CashMakeRequestPopup';
import CashTable from '../../components/specific/CashTable/CashTable';
import UserSearchSelect, { UserValue } from '../../components/common/selector/UserSearchSelect';
import { formatCurrency } from '../../util/format';
import cashTxAPI from "../../api/cashTxAPI";
import constants from "../../constants";
import apiClient from "../../api/apiClient";
import {useMessageStore} from "../../stores/messageStore";
import {useAppStore} from "../../stores/appStore";

const CashManage: React.FC = () => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' })
	const { setMessages } = useStore(useMessageStore);
  const { id } = useParams();
  const navigate = useNavigate();
  // 사용자 요청 리스트
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);
	const fetchCashTxs = cashTxStore((state) => state.fetchCashTxs);
  const setCashTxs = cashTxStore((state) => state.setCashTxs);
  const fetchAllUserCashTotal = cashTxStore((state) => state.fetchAllUserCashTotal);
  const cashTxs = cashTxStore((state) => state.cashTxs);
  const emptySearchUser: UserValue = {
    key: '',
    label: '',
    value: '',
    name: '',
    phoneNum: ''
  }
  // 사용자 검색
  const [searchUser, setSearchUser] = useState<UserValue>(emptySearchUser as UserValue);
  const [searchUserByPhoneNum, setSearchUserByPhoneNum] = useState<UserValue>(emptySearchUser as UserValue);
	// 필터
	const [amount, setAmount] = useState<number | null>(null);
	const [reason, setReason] = useState('');
	const [status, setStatus] = useState<string | undefined>(undefined);

  // 승인/거절 처리 MessageSendPopup
  const [selectedRecord, setSelectedRecord] = useState<CashTx | null>(null);
  const [isRequestApproveModalOpen, setIsRequestApproveModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'approve' | 'reject'>('approve'); //요청 타입
  const updateCashTx = cashTxStore((state) => state.updateCashTx);
  const allUserCashTotal = cashTxStore((state) => state.allUserCashTotal);

  // 충전/출금 요청 생성 MessageSendPopup
  const [isMakeRequestPopupOpen, setIsMakeRequestPopupOpen] = useState(false);
  const [selectedMakeAction, setSelectedMakeAction] = useState<'deposit' | 'withdrawal'>('deposit'); // 충전, 출금 타입
  const createCashTx = cashTxStore((state) => state.createCashTx);
  const isLoading = cashTxStore((state) => state.isLoading);

	useEffect(() => {
		fetchAllUserCashTotal();
		fetchCashTxs({ page: currentPage });
	}, []);

  useEffect(() => {
    if (id) {
      const target = cashTxs.find((record) => record.id === id);
      if (target) {
        setSelectedRecord(target);
        setIsRequestApproveModalOpen(true);
        navigate('/app/business/cashManage', { replace: true });
      }
    }
  }, [id, cashTxs, navigate]);

  // CashApprovePopup 열기
  const showCashRequestPopup = (record: CashTx) => {
    setSelectedRecord(record);
    setIsRequestApproveModalOpen(true);
  }

  // CashApprovePopup 승인/거절 액션 처리
  const okCashRequestPopup = async () => {
    if (!selectedRecord) return;
	  setShouldShowSpinner(true);
    try {
      await updateCashTx({
        id: selectedRecord.id,
        cashTxDTO: selectedRecord,
        action: 'approve',
      });
    } catch (error) {
      console.error('Failed to update record:', error);
    } finally {
	    setShouldShowSpinner(false);
    }
    setIsRequestApproveModalOpen(false);
  };

  const rejectCashRequestPopup = async () => {
    if (!selectedRecord) return;
	  setShouldShowSpinner(true);
    try {
      await updateCashTx({
        id: selectedRecord.id,
        cashTxDTO: selectedRecord,
        action: 'reject',
      });
    } catch (error) {
      console.error('Failed to update record:', error);
    } finally {
	    setShouldShowSpinner(false);
    }
    setIsRequestApproveModalOpen(false);
  };

  // CashApprovePopup 창닫기
  const closeCashRequestPopup = () => {
    setIsRequestApproveModalOpen(false);
  };

  // CashMakeRequestPopup
  const showMakeRequestPopup = async (action: 'deposit' | 'withdrawal') => {
    setSelectedMakeAction(action);
    setIsMakeRequestPopupOpen(true);
  }

  const submitMakeRequestPopup = async (value: CreateCashTxType)=> {
		setShouldShowSpinner(true);
	  await createCashTx(value);
    await fetchCashTxs({ page: currentPage });
    setIsMakeRequestPopupOpen(false);
		setShouldShowSpinner(false);
  }

	const getReasonCodeTxt = (reasonCode: string) => {
		if (reasonCode === 'none') return '없음';
		if (reasonCode === 'first_balju') return '발주지원금';
		if (reasonCode === 'test_user') return '테스트';
		return '';
	};

	const onEdit = async ({ data, reason, reasonCode, accountName }: { data: CashTx, reason?: string, reasonCode?: string, accountName?: string }) => {
		if (!reason && !reasonCode && !accountName) return;
		setShouldShowSpinner(true);

		try {
			await apiClient.put('/api/admin/business/cash/request/edit', {
				cashTxId: data.id,
				reasonCode: reasonCode === 'none' ? null : reasonCode,
				reason,
				accountName,
			});

			setMessages('success', `<<수정 완료>> ${reason ? `사유: ${reason}` : ''} ${reasonCode ? ` / reason_code: ${getReasonCodeTxt(reasonCode)}` : ''} ${accountName ? ` / 입금자명: ${accountName}` : ''}`);

			const cashTxs = await cashTxAPI.fetchCashRequestAPI({
				page: currentPage,
				limit: constants.PAGE_SIZE,
			});
			setCashTxs(cashTxs);
		} catch (e) {
			console.error(e);
			setMessages('error', '오류 발생: reason code 수정');
		} finally {
			setShouldShowSpinner(false);
		}
	};

  const closeMakeRequestPopup = async () => {
    setIsMakeRequestPopupOpen(false);
  }

  const onSearch = async (value: UserValue) => {
    if (!value || !value.key) return;
    setSearchUser(value);
    setSearchUserByPhoneNum(emptySearchUser);
    setCurrentPage(1);
		setAmount(null);
		setReason('');
    await fetchCashTxs({ page: currentPage, userId: value.key});
  }

  const onSearchByPhoneNum = async (value: UserValue) => {
    if (!value || !value.key) return;
    setSearchUser(emptySearchUser);
    setSearchUserByPhoneNum(value);
    setCurrentPage(1);
	  setAmount(null);
	  setReason('');
    await fetchCashTxs({ page: currentPage, userId: value.key});
  }

  // paging
  const changeCurrentPage = async (value: number) => {
    const nextPage = Math.max(currentPage + value, 1);
    setCurrentPage(nextPage);
	  await fetchAllUserCashTotal();
	  const cashTxs = await cashTxAPI.fetchCashRequestAPI({
		  page: nextPage,
		  userId: searchUser.key,
		  amount,
		  reason,
		  limit: constants.PAGE_SIZE,
	  });
	  setCashTxs(cashTxs);
  }

	const formatter: InputNumberProps<number>['formatter'] = (value) => {
		const [start, end] = `${value}`.split('.') || [];
		const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return `${end ? `${v}.${end}` : `${v}`}`;
	};

	const onSearchAmountAndReason = async () => {
		const cashTxs = await cashTxAPI.fetchCashRequestAPI({
			page: currentPage,
			userId: searchUser.key,
			amount,
			reason,
			limit: constants.PAGE_SIZE,
			status,
		});
		setCashTxs(cashTxs);
	};

  const dataSource: CashTx[] = cashTxs ? cashTxs : [];

  return (
    <div>
      <PageTitle title="캐쉬 요청"/>
      {!isLoading && cashTxs ? (
        <div>
          <div
            className={classNames({ [styles.tabletOrMobile]: isTabletOrMobile && isPortrait })}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}
          >
            <div
              className={classNames({ [styles.tabletOrMobile]: isTabletOrMobile })}
              style={{
                display: 'flex',
	              gap: isTabletOrMobile ? '' : '8px'
              }}
            >
              <div style={{ width: '230px' }}>
                <UserSearchSelect selectedUser={searchUser} onChange={onSearch} placeholder="이름 검색" />
              </div>
              <div style={{ width: '230px' }}>
                <UserSearchSelect selectedUser={searchUserByPhoneNum} onChange={onSearchByPhoneNum} placeholder="전화번호 검색" searchMethod="phoneNum" />
              </div>
	            <div style={{ width: '120px' }}>
		            <InputNumber
			            placeholder="금액"
			            step={10000}
			            style={{ width: '100%', textAlign: 'right' }}
			            formatter={formatter}
			            value={amount}
			            onChange={(value) => setAmount(value)}
		            />
	            </div>
	            <div style={{ width: '150px' }}>
		            <Input
			            placeholder="사유"
			            value={reason}
			            allowClear
			            onChange={(e) => setReason(e.target.value)}
		            />
	            </div>
	            <div>
		            <Select
			            placeholder="상태"
			            style={{ width: '120px' }}
			            value={status}
			            options={[
				            { label: '미확인', value: 'hold' },
				            { label: '승인', value: 'done' },
				            { label: '취소/거절', value: 'cancel' },
			            ]}
			            onChange={(value) => setStatus(value)}
			            allowClear
			            onClear={() => setStatus('')}
		            />
	            </div>
	            <div>
		            <Button type="primary" onClick={onSearchAmountAndReason}>검색</Button>
	            </div>

	            <Button color="primary" variant="filled" size="middle" style={{ marginLeft: isTabletOrMobile ? '' : '8px' }}
                      disabled={searchUser.key === emptySearchUser.key
	                      && searchUserByPhoneNum.key === emptySearchUser.key
	                      && amount === null
	                      && reason === ''
	                      && status === ''}
                      onClick={async () => {
	                      await fetchAllUserCashTotal();
												await fetchCashTxs({ page: 1 });

                        setSearchUser(emptySearchUser);
                        setSearchUserByPhoneNum(emptySearchUser);
												setAmount(null);
												setReason('');
												setStatus('');
												setCurrentPage(1);
                      }}>
                초기화
              </Button>
            </div>
            <div>
	            {/*
	            <span style={{
		            alignItems: 'center',
		            height: '32px',
		            padding: '8px 10px', // 내부 여백 추가
		            marginTop: '5px',
		            marginRight: '8px',
		            borderRadius: '8px', // 라운드 형태로 설정
		            backgroundColor: '#e4e2e2', // 배경 색상 추가
	            }}>발주지원금 총액:  {formatCurrency(firstBaljuTotal, 'KRW')}
              </span>
	            <span style={{
		            alignItems: 'center',
		            height: '32px',
		            padding: '8px 10px', // 내부 여백 추가
		            marginTop: '5px',
		            marginRight: '8px',
		            borderRadius: '8px', // 라운드 형태로 설정
		            backgroundColor: '#e4e2e2', // 배경 색상 추가
	            }}>테스트 총액:  {formatCurrency(testUserTotal, 'KRW')}
              </span>
              */}
              <span style={{
                alignItems: 'center',
                height: '32px',
                padding: '8px 10px', // 내부 여백 추가
                marginTop: '5px',
                marginRight: '8px',
                borderRadius: '8px', // 라운드 형태로 설정
                backgroundColor: '#e4e2e2', // 배경 색상 추가
              }}>보유금:  {formatCurrency(allUserCashTotal, 'KRW')}
              </span>
              {/*<Button color="default" variant="dashed" style={{ marginRight: '8px' }}>*/}
              {/*  총보유금: {formatCurrency(allUserCashTotal, 'KRW')}*/}
              {/*</Button>*/}
              <Button color="primary" variant="filled" size="middle" style={{ marginRight: '8px' }} onClick={() => showMakeRequestPopup('deposit')}>
                +충전
              </Button>
              <Button color="danger" variant="filled" size="middle" onClick={() => showMakeRequestPopup('withdrawal')}>
                +출금
              </Button>
            </div>
          </div>
	        <div>
		        <CashTable showCashRequestPopup={showCashRequestPopup} dataSource={dataSource} />
	        </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '16px'
          }}>
            <div></div>
            <SimplePaging currentPage={currentPage}
                          dataLength={cashTxs.length}
                          onClickPrePageBtn={async () => changeCurrentPage(-1)}
                          onClickNextPageBtn={async () => changeCurrentPage(1)} />
          </div>
          {/* 충전 또는 출금 버튼 눌렀을 때 */}
          {isMakeRequestPopupOpen &&
            <CashMakeRequestPopup
              isModalOpen={isMakeRequestPopupOpen}
              onSubmit={submitMakeRequestPopup}
              onClose={closeMakeRequestPopup}
              selectedAction={selectedMakeAction}
            />
          }
          {/* 승인, 취소, 미확인, 거절 등 눌렀을 때 */}
          {isRequestApproveModalOpen && selectedRecord &&
            <CashApprovePopup
              isModalOpen={isRequestApproveModalOpen}
              handleOk={okCashRequestPopup}
              handleReject={rejectCashRequestPopup}
              handleCancel={closeCashRequestPopup}
              handleEdit={onEdit}
              selectedRecord={selectedRecord}
              selectedAction={selectedAction}
              onChangeReason={(reason) => {
                setSelectedRecord((prev) => prev
                  ? ({ ...prev, reason })
                  : null)
              }}
              onChangeReasonCode={(reasonCode) => {
	              setSelectedRecord((prev) => prev
		              ? ({ ...prev, reasonCode })
		              : null)
              }}
              onChangeAccountName={(accountName) => {
	              setSelectedRecord((prev) => prev
		              ? ({ ...prev, accountName })
		              : null)
              }}
            />
          }
        </div>
      ) : (
        <LoadingBar/>
      )}
    </div>
  );
};

export default CashManage;
